import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivitiesTabDisplayService } from '@app/activities/activities-tab-display.service';
import { ProfileTabDisplayService } from '@app/profile/profile-tab-display.service';
import { TeacherHomeTabDisplayService } from '@app/teacher-home/teacher-home-tab-display.service';
import { AdminHomeTabDisplayService } from '@app/admin-home/admin-home-tab-display.service';
import { TeacherDashboardsTabDisplayService } from '@app/teacher-dashboards/teacher-dashboards-tab-display.service';
import { ReportsTabDisplayService } from '@app/reports/reports-tab-display.service';
import { TeacherUsersTabDisplayService } from '@app/teacher-users/teacher-users-tab-display.service';

import { Store } from '@ngrx/store';
import { State } from '@wam/shared';

import { AccountsTabDisplayService } from '@app/accounts/accounts-tab-display.service';
import { ConfigurationTabDisplayService } from '@app/configuration-management/configuration-tab-display.service';
import { GoalsTabDisplayService } from '@app/goals/goals-tab-display.service';
import { OrganizationsTabDisplayService } from '@app/organizations/organizations-tab-display.service';
import { NotificationsManagerTabDisplayService } from '@app/notifications-manager/notifications-manager-tab-display.service';
import { MessagesAndNotificationsTabDisplayService } from '@app/messages-and-notifications/messages-and-notifications-tab-display.service';
import { FamiliesTabDisplayService } from '@app/families/families-tab-display.service';
import { AssessmentsTabDisplayService } from '@app/assessments/assessments-tab-display.service';

@Injectable({
  providedIn: 'root',
})
export class TabDisplayAggregatorService {
  private store = inject<Store<State>>(Store);
  private ACCOUNTS = inject(AccountsTabDisplayService);
  private ACTIVITIES = inject(ActivitiesTabDisplayService);
  private ADMIN_HOME = inject(AdminHomeTabDisplayService);
  private ASSESSMENTS = inject(AssessmentsTabDisplayService);
  private CONFIGURATION = inject(ConfigurationTabDisplayService);
  private FAMILIES = inject(FamiliesTabDisplayService);
  private GOALS = inject(GoalsTabDisplayService);
  private MESSAGES_AND_NOTIFICATIONS = inject(MessagesAndNotificationsTabDisplayService);
  private ORGANIZATIONS = inject(OrganizationsTabDisplayService);
  private PROFILE = inject(ProfileTabDisplayService);
  private REPORTS = inject(ReportsTabDisplayService);
  private TEACHER_DASHBOARDS = inject(TeacherDashboardsTabDisplayService);
  private TEACHER_HOME = inject(TeacherHomeTabDisplayService);
  private TEACHER_USERS = inject(TeacherUsersTabDisplayService);
  private NOTIFICATIONS_MANAGER = inject(NotificationsManagerTabDisplayService);

  get properties(): string[] {
    return Object.getOwnPropertyNames(this).filter((name) => name !== 'store');
  }

  get displayedTabs$(): Observable<string[]> {
    const displayedTabs: Observable<string | null>[] = this.properties.map((name) =>
      this[name].shouldShow().pipe(map((show: boolean) => (show ? name : null))),
    );

    return combineLatest(displayedTabs).pipe(
      map((tabs) => tabs.filter((tab: string) => tab !== null)),
    );
  }

  get firstTab$(): Observable<string> {
    const firstTabs: Observable<string | null>[] = this.properties.map((name) =>
      this[name].isFirst().pipe(map((first: boolean) => (first ? name : null))),
    );

    return combineLatest(firstTabs).pipe(map((tabs) => tabs.find((tab: string) => tab !== null)));
  }

  getDisplayedTabs(): Observable<string[]> {
    return this.displayedTabs$;
  }

  getFirstTab(): Observable<string> {
    return this.firstTab$;
  }

  getNotificationCountForTab(name: string): Observable<number> {
    if (this.properties.includes(name)) {
      return this[name].notificationCount?.().pipe(map((count: number) => count ?? 0)) ?? of(0);
    }

    return of(0);
  }
}
